import classNames from 'classnames';
import { capitalize } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Breadcrumb.scss';

/**
 * @typedef Segment
 * @property {string} link
 * @property {string} title
 * @property {string?} icon
 *
 * @typedef BreadcrumbProps
 * @property {Segment[]} path
 * @property {boolean} autoWire
 *
 * @param { BreadcrumbProps } props
 */
const Breadcrumb = ({ path, autoWire, className }) => {
  const location = useLocation();

  const fullPath = useMemo(() => {
    const homeSegment = {
      link: '',
      icon: 'home'
    };
    if (autoWire) {
      return location.pathname.split('/').map((segment, index) => {
        if (index === 0) return homeSegment;
        return ({
          link: segment,
          title: capitalize(segment),
        });
      });
    }
    return [homeSegment].concat(path);
  }, [path, autoWire, location]);

  const isActivePath = useCallback((pathname) => pathname === location.pathname, [location]);

  return <nav aria-label="breadcrumb">
    <ol className={classNames(`breadcrumb align-items-center`, className)}>
      {
        fullPath.map((segment, index) => {
          const currentPathname = `${fullPath.slice(0, index + 1).map(p => p.link).join('/')}`;
          const active = isActivePath(currentPathname);
          const className = classNames('breadcrumb-item', { active });

          const iconClassName = classNames(`fal fa-${segment.icon}`, {
            'mr-2': segment.title
          });

          const content = <React.Fragment>
            {segment.title}
            {segment.icon && <i className={iconClassName}></i>}
          </React.Fragment>;

          return <React.Fragment key={segment.link}>
            {/* Separator icon */}
            {index !== 0 && <i className="fal fa-chevron-right mx-3"></i>}
            <li className={className} aria-current={active ? "page" : null}>
              {active
                ? content
                : <Link to={currentPathname}>
                  {content}
                </Link>
              }
            </li>
          </React.Fragment>;
        })
      }
    </ol>
  </nav>;
};

Breadcrumb.propTypes = {
  path: PropTypes.arrayOf(PropTypes.shape({
    link: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
  })),
  autoWire: PropTypes.bool,
  className: PropTypes.any,
};

Breadcrumb.defaultProps = {
  path: [],
  autoWire: false,
  className: null
};

export default React.memo(Breadcrumb);